/**
 * Theme Builder form_root design presets and scoped CSS for public + canvas.
 * Authors configure via `FormRootElement.design` in the inspector.
 *
 * Visual rules target `[data-pb-node="{formRootId}"]` (beat-theme selectors) so
 * Theme Manager tag/`a`/`label` `!important` rules cannot wash out public preview.
 * Layout modifiers still use `.pb-form-*` classes on the same `<form>`.
 */

export type {
	FormDesignPreset,
	FormFieldsLayout,
	FormInputVariant,
	FormRootDesignSettings,
	FormSpacingDensity,
	FormSubmitButtonStyle,
} from "../ui/types";

import type {
	FormDesignPreset,
	FormFieldsLayout,
	FormInputVariant,
	FormRootDesignSettings,
	FormSpacingDensity,
	FormSubmitButtonStyle,
} from "../ui/types";
import {
	pbNodeBeatThemeDescendantSelector,
	pbNodeBeatThemeSelector,
} from "./elementTypographyOverridesCss";

export interface ResolvedFormDesign {
	disclaimer?: string;
	fieldsLayout: FormFieldsLayout;
	heading?: string;
	inputVariant: FormInputVariant;
	preset: FormDesignPreset;
	spacing: FormSpacingDensity;
	submitButtonStyle: FormSubmitButtonStyle;
	subtitle?: string;
}

const SPACING_GAP: Record<FormSpacingDensity, string> = {
	compact: "12px",
	comfortable: "20px",
	spacious: "28px",
};

const SPACING_FIELD_GAP: Record<FormSpacingDensity, string> = {
	compact: "4px",
	comfortable: "6px",
	spacious: "8px",
};

export function parseFormDesignJson(
	raw: string | undefined
): FormRootDesignSettings | undefined {
	if (!raw?.trim()) {
		return undefined;
	}
	try {
		const v = JSON.parse(raw) as unknown;
		if (typeof v !== "object" || v === null || Array.isArray(v)) {
			return undefined;
		}
		return v as FormRootDesignSettings;
	} catch {
		return undefined;
	}
}

export function resolveFormDesign(
	settings?: FormRootDesignSettings | null
): ResolvedFormDesign {
	return {
		preset: settings?.preset ?? "default",
		heading: settings?.heading?.trim() || undefined,
		subtitle: settings?.subtitle?.trim() || undefined,
		disclaimer: settings?.disclaimer?.trim() || undefined,
		fieldsLayout: settings?.fieldsLayout ?? "stacked",
		inputVariant: settings?.inputVariant ?? "outline",
		spacing: settings?.spacing ?? "comfortable",
		submitButtonStyle: settings?.submitButtonStyle ?? "default",
	};
}

/** Safe CSS class suffix from builder node id. */
export function formDesignScopeId(nodeId: string): string {
	const safe = nodeId.replace(/[^a-zA-Z0-9_-]/g, "");
	return safe.length > 0 ? safe : "form";
}

export function formDesignRootClassName(nodeId: string): string {
	return `pb-form pb-form-${formDesignScopeId(nodeId)}`;
}

export function formDesignClassNames(
	nodeId: string,
	design: ResolvedFormDesign
): string {
	const parts = [
		formDesignRootClassName(nodeId),
		`pb-form-preset-${design.preset}`,
		`pb-form-input-${design.inputVariant}`,
		`pb-form-spacing-${design.spacing}`,
	];
	if (design.fieldsLayout === "two-column") {
		parts.push("pb-form-two-column");
	}
	if (design.submitButtonStyle !== "default") {
		parts.push(`pb-form-submit-${design.submitButtonStyle}`);
	}
	return parts.join(" ");
}

/** Whether a field type should span full width in two-column layout. */
export function formFieldSpansFullWidth(
	fieldType: string | undefined
): boolean {
	if (!fieldType) {
		return false;
	}
	return (
		fieldType === "textarea" ||
		fieldType === "radio" ||
		fieldType === "select" ||
		fieldType === "checkbox"
	);
}

/**
 * Scoped CSS for form design presets.
 *
 * Critical colors / typography use {@link pbNodeBeatThemeSelector} so Region
 * Theme Manager tag rules (e.g. `a { color: … !important }`) cannot blank
 * consent copy or override submit fills. Class hooks remain for layout chrome.
 */
export function buildFormDesignScopedCss(
	nodeId: string,
	design: ResolvedFormDesign
): string {
	const classScope = `.pb-form-${formDesignScopeId(nodeId)}`;
	const root = pbNodeBeatThemeSelector(nodeId);
	const d = (descendant: string) =>
		pbNodeBeatThemeDescendantSelector(nodeId, descendant);
	/** Each comma-separated fragment gets its own beat-theme prefix. */
	const dAll = (...descendants: string[]) =>
		descendants.map((part) => d(part.trim())).join(",");
	const gap = SPACING_GAP[design.spacing];
	const fieldGap = SPACING_FIELD_GAP[design.spacing];

	const fieldControl = dAll(
		"[data-pb-form-field] input:not([type=checkbox]):not([type=radio]):not([type=hidden])",
		"[data-pb-form-field] textarea",
		"[data-pb-form-field] select"
	);
	const fieldControlFocus = dAll(
		"[data-pb-form-field] input:not([type=checkbox]):not([type=radio]):focus",
		"[data-pb-form-field] textarea:focus",
		"[data-pb-form-field] select:focus"
	);
	const submitCtl = d("[data-pb-form-submit]");

	const lines: string[] = [
		`${root},${classScope}{--pb-form-gap:${gap};--pb-form-field-gap:${fieldGap};font-family:var(--tt-font-sans,system-ui,sans-serif);}`,
		`${d(".pb-form-heading")}{margin:0 0 8px;font-size:clamp(1.375rem,2.5vw,1.75rem);font-weight:600;line-height:1.25;color:var(--tt-color-fg,#0f172a)!important;font-family:var(--tt-font-sans,system-ui,sans-serif)!important;}`,
		`${d(".pb-form-subtitle")}{margin:0 0 ${gap};font-size:0.9375rem;line-height:1.5;color:var(--tt-color-muted-foreground,#64748b)!important;font-family:inherit;}`,
		`${d(".pb-form-disclaimer")}{margin:${fieldGap} 0 0;font-size:0.75rem;line-height:1.45;color:var(--tt-color-muted-foreground,#64748b)!important;font-family:inherit;}`,
		`${d("[data-pb-form-field]")}{display:flex;flex-direction:column;gap:var(--pb-form-field-gap);}`,
		`${dAll("[data-pb-form-field] label", "[data-pb-form-consent] label")}{font-size:0.875rem;font-weight:500;color:var(--tt-color-fg,#334155)!important;line-height:1.4;font-family:inherit;}`,
		/* Consent body + Privacy-policy links must inherit label color (Theme `a` is often white). */
		`${dAll(
			"[data-pb-form-field] label span",
			"[data-pb-form-consent] label span",
			"[data-pb-form-consent] label a",
			"[data-pb-form-field] label a"
		)}{color:inherit!important;}`,
		`${d("[data-pb-form-field] [role=radiogroup] label span")}{flex:1 1 auto;min-width:0;color:inherit;}`,
		`${d(".pb-form-field-help")}{font-size:0.75rem;line-height:1.35;color:var(--tt-color-muted-foreground,#64748b)!important;margin:0;}`,
		`${fieldControl}{width:100%;box-sizing:border-box;font-size:1rem;line-height:1.5;font-family:var(--tt-font-sans,system-ui,sans-serif)!important;color:var(--tt-color-fg,#0f172a)!important;transition:border-color 0.15s ease,box-shadow 0.15s ease;}`,
		`${dAll(
			"[data-pb-form-field] .pb-form-field-error",
			"[data-pb-form-consent] .pb-form-field-error"
		)}{font-size:0.75rem;line-height:1.35;color:var(--tt-color-destructive,#b91c1c)!important;margin:0;}`,
		`${dAll(
			'[data-pb-form-field] input[aria-invalid="true"]',
			'[data-pb-form-field] textarea[aria-invalid="true"]',
			'[data-pb-form-field] select[aria-invalid="true"]',
			'[data-pb-form-consent] input[aria-invalid="true"]'
		)}{border-color:var(--destructive,#b91c1c)!important;box-shadow:0 0 0 1px color-mix(in srgb,var(--destructive,#b91c1c) 45%,transparent)!important;}`,
		`${submitCtl}{font-family:var(--tt-font-sans,system-ui,sans-serif)!important;}`,
	];

	if (design.inputVariant === "filled") {
		lines.push(
			`${fieldControl}{background-color:var(--tt-color-muted-bg,#f1f5f9);border:1px solid transparent;border-radius:8px;padding:12px 14px;}`,
			`${fieldControlFocus}{outline:none;border-color:var(--tt-color-primary,#4f46e5);box-shadow:0 0 0 3px color-mix(in srgb,var(--tt-color-primary,#4f46e5) 18%,transparent);}`
		);
	} else {
		lines.push(
			`${fieldControl}{background-color:var(--tt-color-bg,#fff);border:1px solid color-mix(in srgb,var(--tt-color-fg,#0f172a) 14%,transparent);border-radius:8px;padding:11px 14px;}`,
			`${fieldControlFocus}{outline:none;border-color:var(--tt-color-primary,#4f46e5);box-shadow:0 0 0 3px color-mix(in srgb,var(--tt-color-primary,#4f46e5) 18%,transparent);}`
		);
	}

	/*
	 * Submit fill/radius stay on `.pb-form-preset-*` class compounds so author
	 * inspector colors (collectElementAppearanceCss → [data-pb-node=submit]) can
	 * still win when customized. Palette-default submit colors are omitted from
	 * appearance CSS so these preset rules apply on public preview.
	 */
	if (design.preset === "minimal") {
		lines.push(
			`${root}.pb-form-preset-minimal,${classScope}.pb-form-preset-minimal{background:transparent;padding:0;border:none;box-shadow:none;}`,
			`${classScope}.pb-form-preset-minimal [data-pb-form-submit]{background-color:var(--tt-color-primary,#4f46e5)!important;color:var(--tt-color-primary-foreground,#fff)!important;border:none;font-weight:600;padding:12px 28px;border-radius:9999px;cursor:pointer;}`,
			`${classScope}.pb-form-preset-minimal [data-pb-form-submit]:hover{filter:brightness(0.95);}`
		);
	} else if (design.preset === "card") {
		lines.push(
			`${root}.pb-form-preset-card,${classScope}.pb-form-preset-card{background-color:var(--tt-color-bg,#fff);padding:clamp(24px,4vw,40px);border-radius:16px;box-shadow:0 8px 32px color-mix(in srgb,var(--tt-color-fg,#0f172a) 10%,transparent);border:1px solid color-mix(in srgb,var(--tt-color-fg,#0f172a) 6%,transparent);}`,
			`${classScope}.pb-form-preset-card [data-pb-form-submit]{background-color:var(--tt-color-fg,#0f172a)!important;color:var(--tt-color-bg,#fff)!important;border:none;font-weight:600;padding:14px 32px;border-radius:9999px;cursor:pointer;}`,
			`${classScope}.pb-form-preset-card [data-pb-form-submit]:hover{opacity:0.92;}`
		);
	} else if (design.preset === "bordered") {
		lines.push(
			`${root}.pb-form-preset-bordered,${classScope}.pb-form-preset-bordered{background-color:var(--tt-color-bg,#fff);padding:24px;border-radius:12px;border:1px solid color-mix(in srgb,var(--tt-color-fg,#0f172a) 12%,transparent);}`,
			`${classScope}.pb-form-preset-bordered [data-pb-form-submit]{background-color:var(--tt-color-primary,#4f46e5)!important;color:var(--tt-color-primary-foreground,#fff)!important;border:none;font-weight:600;padding:12px 24px;border-radius:8px;cursor:pointer;}`
		);
	} else {
		lines.push(
			`${root}.pb-form-preset-default,${classScope}.pb-form-preset-default{background-color:var(--tt-color-bg,#fff);padding:24px;border-radius:12px;border:1px solid color-mix(in srgb,var(--tt-color-fg,#0f172a) 8%,transparent);}`,
			`${classScope}.pb-form-preset-default [data-pb-form-submit]{background-color:var(--tt-color-primary,#4f46e5)!important;color:var(--tt-color-primary-foreground,#fff)!important;border:none;font-weight:600;padding:12px 28px;border-radius:8px;cursor:pointer;}`,
			`${classScope}.pb-form-preset-default [data-pb-form-submit]:hover{filter:brightness(0.96);}`
		);
	}

	if (design.fieldsLayout === "two-column") {
		lines.push(
			`${root}.pb-form-two-column,${classScope}.pb-form-two-column{display:grid;grid-template-columns:1fr 1fr;gap:var(--pb-form-gap);align-items:start;}`,
			`${classScope}.pb-form-two-column .pb-form-heading,${classScope}.pb-form-two-column .pb-form-subtitle,${classScope}.pb-form-two-column .pb-form-disclaimer,${classScope}.pb-form-two-column [data-pb-form-field-full],${classScope}.pb-form-two-column [data-pb-form-submit],${classScope}.pb-form-two-column [data-pb-form-consent],${classScope}.pb-form-two-column > :not([data-pb-form-field]){grid-column:1/-1;}`,
			`@media (max-width:640px){${root}.pb-form-two-column,${classScope}.pb-form-two-column{grid-template-columns:1fr;}}`
		);
	} else {
		lines.push(
			`${root}:not(.pb-form-two-column),${classScope}:not(.pb-form-two-column){display:flex;flex-direction:column;gap:var(--pb-form-gap);}`
		);
	}

	if (design.submitButtonStyle === "pill") {
		lines.push(
			`${classScope}.pb-form-submit-pill [data-pb-form-submit],${submitCtl}{border-radius:9999px!important;}`
		);
	} else if (design.submitButtonStyle === "pill-full") {
		lines.push(
			`${classScope}.pb-form-submit-pill-full [data-pb-form-submit],${submitCtl}{border-radius:9999px!important;width:100%;display:block;text-align:center;}`
		);
	}

	return lines.join("\n");
}
